'use client'

import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import type { Locale } from '@/i18n'
import { createMilitaryFormSchema, type MilitaryFormSchema } from '@/lib/validations/application'
import type { Vacancy } from '@/payload-types'
import { useApplicationStore } from '@/stores/applicationStore'
import { useApplicationModalStore } from '@/stores/applicationModalStore'

import { getMilitaryFormTranslations } from './constants'
import { trackMilitaryApplicationSubmit, trackApplicationFormError } from '@/utilities/analytics'

interface MilitaryFormProps {
  locale: Locale
  positionSlug?: string
}

export function MilitaryForm({ locale, positionSlug }: MilitaryFormProps) {
  const [mounted, setMounted] = useState(false)
  const store = useApplicationStore()
  const { closeModal } = useApplicationModalStore()
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submittedName, setSubmittedName] = useState<string>('')

  useEffect(() => {
    setMounted(true)
  }, [])

  const militaryForm = mounted ? store.militaryForm : {}
  const setMilitaryForm = store.setMilitaryForm

  const form = useForm<MilitaryFormSchema>({
    resolver: zodResolver(createMilitaryFormSchema(locale)),
    defaultValues: {
      fullName: '',
      age: undefined,
      militaryRank: '',
      bzpvCompleted: undefined,
      szcStatus: undefined,
      phone: '',
      position: '',
    },
  })

  useEffect(() => {
    if (mounted && militaryForm) {
      Object.entries(militaryForm).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          form.setValue(key as keyof MilitaryFormSchema, value as any)
        }
      })
    }
  }, [mounted, militaryForm, form])

  useEffect(() => {
    if (positionSlug && !militaryForm?.position) {
      const fetchVacancy = async () => {
        try {
          const response = await fetch(
            `/api/vacancies?where[slug][equals]=${positionSlug}&locale=${locale}`,
          )
          const data = await response.json()
          const vacancy = data.docs?.[0] as Vacancy | undefined
          if (vacancy?.title) {
            form.setValue('position', vacancy.title)
          }
        } catch (error) {
          console.error('Failed to fetch vacancy:', error)
        }
      }
      fetchVacancy()
    }
  }, [locale, positionSlug, militaryForm?.position, form])

  const onSubmit = async (data: MilitaryFormSchema) => {
    setSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(false)

    try {
      const formattedData = {
        applicationType: 'military',
        ...data,
      }

      const response = await fetch('/api/applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formattedData),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.errors?.[0]?.message || 'Failed to submit application')
      }

      // Track successful submission
      trackMilitaryApplicationSubmit(data.position || positionSlug)

      setMilitaryForm(data)
      setSubmittedName(data.fullName)
      setSubmitSuccess(true)
      form.reset()

      setTimeout(() => closeModal(), 2000)
    } catch (error) {
      console.error('Failed to submit application:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit application'
      setSubmitError(errorMessage)
      // Track form error
      trackApplicationFormError(errorMessage, 'military')
    } finally {
      setSubmitting(false)
    }
  }

  const t = getMilitaryFormTranslations(locale)

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="bg-[#1a1a1a] rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t.fullName} <span className="text-red-500">{t.required}</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder={t.fullNamePlaceholder} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t.phone} <span className="text-red-500">{t.required}</span>
                  </FormLabel>
                  <FormControl>
                    <PhoneInput placeholder={t.phonePlaceholder} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="militaryRank"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t.militaryRank} <span className="text-red-500">{t.required}</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder={t.militaryRankPlaceholder} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="age"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t.age}</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder={t.agePlaceholder}
                      maxLength={3}
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '')
                        if (value === '') {
                          field.onChange(undefined)
                        } else {
                          const numValue = parseInt(value, 10)
                          if (numValue <= 100) {
                            field.onChange(numValue)
                          }
                        }
                      }}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="position"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t.position} <span className="text-red-500">{t.required}</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder={t.positionPlaceholder} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="bzpvCompleted"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>
                    {t.bzpvCompleted} <span className="text-red-500">{t.required}</span>
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex flex-row space-x-4"
                    >
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="yes" />
                        </FormControl>
                        <FormLabel className="font-normal">{t.yes}</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="no" />
                        </FormControl>
                        <FormLabel className="font-normal">{t.no}</FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="szcStatus"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>
                    {t.szcStatus} <span className="text-red-500">{t.required}</span>
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex flex-row space-x-4"
                    >
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="yes" />
                        </FormControl>
                        <FormLabel className="font-normal">{t.yes}</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="no" />
                        </FormControl>
                        <FormLabel className="font-normal">{t.no}</FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {submitError && (
          <div className="bg-red-500 border border-red-500 text-red-100 rounded-lg p-4 font-medium">
            {submitError}
          </div>
        )}

        {submitSuccess && (
          <div className="bg-green-500 border border-green-500 text-green-100 rounded-lg p-4 font-medium">
            {locale === 'uk'
              ? `Заявку успішно надіслано, ${submittedName}! Ми зв'яжемося з вами найближчим часом.`
              : `Application submitted successfully, ${submittedName}! We will contact you soon.`}
          </div>
        )}

        <Button
          type="submit"
          size="lg"
          className="w-full bg-[hsl(133.18deg_52.25%_26.48%)] hover:bg-[hsl(133.18deg_65%_35%)] text-white"
          disabled={submitting || submitSuccess}
        >
          {submitSuccess
            ? locale === 'uk'
              ? 'Заявку надіслано ✓'
              : 'Application Sent ✓'
            : submitting
              ? locale === 'uk'
                ? 'Надсилання...'
                : 'Submitting...'
              : t.submit}
        </Button>
      </form>
    </Form>
  )
}
